import { useState } from "react";
import {
    VisualAssetReference,
    buildDocumentFileUrl,
    buildVisualAssetPreviewUrl,
} from "../../api/documentAgent";

type Props = {
    asset: VisualAssetReference;
    conversationId: string;
};

export default function InlineVisualAssetPreview({ asset, conversationId }: Props) {
    const [loadFailed, setLoadFailed] = useState(false);

    const fileUrl = buildDocumentFileUrl(asset.documentId, conversationId);
    const previewUrl = buildVisualAssetPreviewUrl(asset.documentId, asset.assetId, conversationId);
    const openUrl = `${fileUrl}#page=${Math.max(1, asset.pageNumber)}`;
    const accessibleLabel = asset.label || `${asset.assetType} from page ${asset.pageNumber}`;

    return (
        <section className="mt-4 overflow-hidden rounded-xl border border-primary_one-100 bg-black/20">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-primary_one-100 px-3 py-2">
                <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">
                        {asset.label || `${asset.assetType} · page ${asset.pageNumber}`}
                    </p>
                    <p className="truncate text-xs text-gray-400">
                        {asset.documentTitle} · p. {asset.pageNumber}
                        {asset.assetGroupKey ? ` · part ${asset.sequenceIndex + 1}` : ""}
                    </p>
                </div>
                <a
                    href={openUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded border border-primary_one-100 px-2 py-1 text-xs font-semibold text-primary_one-200 hover:border-primary_one-200"
                >
                    View full page
                </a>
            </div>

            <div className="w-full overflow-auto bg-white p-2">
                {loadFailed ? (
                    <div className="p-4 text-sm text-gray-700">
                        The inline preview could not be rendered. Use “View full page” to open the original PDF.
                    </div>
                ) : (
                    <img
                        src={previewUrl}
                        alt={accessibleLabel}
                        loading="lazy"
                        className="mx-auto max-h-[70vh] w-full object-contain"
                        onError={() => setLoadFailed(true)}
                    />
                )}
            </div>
            {asset.useFullPageFallback && (
                <p className="px-3 py-2 text-xs text-gray-400">
                    Qouda was not confident enough in the crop, so the full source page is shown.
                </p>
            )}
        </section>
    );
}
